import { useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import {
  GET_ORGANIZATION_PLOT_GROUPS_EII_STATS_PATH,
  GET_ORGANIZATION_PLOT_GROUPS_PATH,
  GetOrganizationPlotGroupEIIStatsErrorDto,
  GetOrganizationPlotGroupEIIStatsSuccessDto,
  GetOrganizationPlotGroupsErrorDto,
  GetOrganizationPlotGroupsSuccessDto,
} from '@/api/rest/resources/types/organization';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { assertIsDefined } from '@/utils/assert';
import { isNullish } from '@/utils/booleans';

type SuccessDto = GetOrganizationPlotGroupsSuccessDto;
type ErrorDto = GetOrganizationPlotGroupsErrorDto;

type EIIStatsSuccessDto = GetOrganizationPlotGroupEIIStatsSuccessDto;
type EIIStatsErrorDto = GetOrganizationPlotGroupEIIStatsErrorDto;

export const useOrganizationPlotGroups = (organizationIdArg: string) => {
  const id = organizationIdArg;

  assertIsDefined(id, 'organizationId');

  const queryFn = () => {
    return getRequest<SuccessDto>(GET_ORGANIZATION_PLOT_GROUPS_PATH, { id });
  };

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: ['organization', id, 'plot-groups'],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};

export const useOrganizationPlotGroup = (organizationId: string, plotGroupId?: string | null) => {
  const plotGroups = useOrganizationPlotGroups(organizationId).data;

  if (isNullish(plotGroupId)) {
    return null;
  }
  return plotGroups.find((group) => group.id === plotGroupId);
};

export const useOrganizationPlotGroupEIIStats = (organizationIdArg: string) => {
  const orgId = useOrganizationId();
  const organizationId = organizationIdArg ?? orgId;

  assertIsDefined(organizationId, 'organizationId');

  const queryFn = () => {
    return getRequest<EIIStatsSuccessDto>(GET_ORGANIZATION_PLOT_GROUPS_EII_STATS_PATH, {
      organization_id: organizationId,
    });
  };

  return useSuspenseQuery<EIIStatsSuccessDto, EIIStatsErrorDto>({
    queryKey: ['organization', organizationId, 'plot-groups-eii-stats'],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};
